import { useState } from "react";
import { User, Building, Phone, Mail, Lock, Loader2, Save, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", business_name: user?.business_name || "", phone: user?.phone || "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/auth/profile", form);
      if (updateUser) updateUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setChangingPw(true);
    try {
      await API.put("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password changed successfully");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to change password"); }
    finally { setChangingPw(false); }
  };

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Profile Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account details</p>
      </div>

      {/* Avatar card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-[#091629] flex items-center justify-center text-white text-2xl font-extrabold shadow-md">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div>
          <p className="text-base font-bold text-slate-900">{user?.name}</p>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <p className="text-xs text-[#0c1e41] font-medium capitalize mt-1">{user?.role?.replace("_", " ")}</p>
        </div>
      </div>

      {/* Profile info */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-5 flex items-center gap-2">
          <User size={16} className="text-[#0c1e41]" /> Personal Information
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Full Name</Label>
            <Input value={form.name} onChange={(e) => setForm({...form,name:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Email Address</Label>
            <Input value={user?.email || ""} disabled className="bg-slate-50 border-slate-200 text-slate-500 h-10 cursor-not-allowed" />
            <p className="text-xs text-slate-400">Contact support to change your email address</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Business Name</Label>
            <Input value={form.business_name} onChange={(e) => setForm({...form,business_name:e.target.value})} placeholder="Acme Nigeria Ltd" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Phone Number</Label>
            <Input value={form.phone} onChange={(e) => setForm({...form,phone:e.target.value})} placeholder="08012345678" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
          </div>
          <Button onClick={saveProfile} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm">
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-5 flex items-center gap-2">
          <Lock size={16} className="text-[#0c1e41]" /> Change Password
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Current Password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={pwForm.current_password} onChange={(e) => setPwForm({...pwForm,current_password:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10 pr-10" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">New Password</Label>
            <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({...pwForm,new_password:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm">Confirm New Password</Label>
            <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({...pwForm,confirm:e.target.value})} className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
          </div>
          <Button onClick={changePassword} disabled={changingPw || !pwForm.current_password || !pwForm.new_password} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm">
            {changingPw ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            Change Password
          </Button>
        </div>
      </div>
    </div>
  );
}
